import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { useStore } from '../store';
import { shared } from './shared';
import { fetchProfile, synthesize } from '../api';

// Five ideological clusters — the Eagle speaks a short profile as you approach each group.
const CLUSTERS = [
  { id: 'progressive-left', title: 'Progressive Left', x: -120, z: -80 },
  { id: 'establishment-liberal', title: 'Establishment Liberal', x: -40, z: -45 },
  { id: 'ambivalent-right', title: 'Ambivalent Right', x: 40, z: 45 },
  { id: 'faith-and-flag', title: 'Faith and Flag', x: 120, z: 80 },
  { id: 'stressed-sideliners', title: 'Stressed Sideliners', x: 0, z: 21 },
];

export default function ProfileGuide() {
  const last = useRef(null);
  const busy = useRef(false);
  const acc = useRef(0);
  const audio = useRef(null);

  useFrame((_, delta) => {
    acc.current += delta;
    if (acc.current < 0.3) return;
    acc.current = 0;
    if (!audio.current) audio.current = new Audio();

    const s = useStore.getState();
    // Guide must be 100% silent under Master Mute OR Mute Guide — enforce on the
    // element every frame (kills any in-flight clip) and never trigger while muted.
    const guideMuted = !s.audioEnabled || s.guideVolume < 0.01;
    audio.current.volume = guideMuted ? 0 : Math.max(0, Math.min(1, s.guideVolume));
    audio.current.muted = guideMuted;

    if (!s.entered || s.tourActive || s.narrating || busy.current || guideMuted) return;

    const cam = shared.camPos;
    let near = null, best = 30;
    for (const c of CLUSTERS) {
      const d = Math.hypot(c.x - cam.x, c.z - cam.z);
      if (d < best) { best = d; near = c; }
    }
    if (!near) { last.current = null; return; }
    if (near.id === last.current) return;
    last.current = near.id;

    busy.current = true;
    (async () => {
      const st = useStore.getState();
      try {
        st.setNarrating(true);
        st.setBankFocus(near.z < 0 ? 'progressive' : near.z > 22 ? 'traditional' : null);
        st.setCaption(`Approaching the ${near.title}…`, 'THE HARPY EAGLE');
        const p = await fetchProfile(near.id);
        st.setCaption(p.text, `PROFILE · ${near.title.toUpperCase()}`);
        let url = null;
        try { url = await synthesize(p.text, 'sage'); } catch (e) {}
        await new Promise((res) => {
          const a = audio.current;
          const done = () => res();
          const to = setTimeout(done, 32000);
          a.onended = () => { clearTimeout(to); res(); };
          a.onerror = () => { clearTimeout(to); res(); };
          if (url) { a.src = url; a.play().catch(done); } else { setTimeout(done, 9000); }
        });
      } catch (e) {
        /* ignore */
      } finally {
        st.setNarrating(false);
        st.setBankFocus(null);
        busy.current = false;
      }
    })();
  });

  return null;
}
